import { Helmet } from 'react-helmet-async';

interface FAQ {
  question: string;
  answer: string;
  category?: string;
}

interface FAQSchemaProps {
  faqs: FAQ[];
  pageTitle?: string;
  pageDescription?: string;
}

const FAQSchema = ({ faqs, pageTitle, pageDescription }: FAQSchemaProps) => {
  if (!faqs || faqs.length === 0) return null;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "name": pageTitle || "Frequently Asked Questions - SeniorThrive",
    "description": pageDescription || "Common questions about SeniorThrive's platform for aging in place safely and independently.",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      },
      ...(faq.category && { "about": faq.category })
    }))
  };

  // Also create HowTo schema for actionable FAQs
  const howToFAQs = faqs.filter(faq => 
    faq.question.toLowerCase().includes('how to') || 
    faq.question.toLowerCase().includes('how do') ||
    faq.answer.includes('step') ||
    faq.answer.includes('follow')
  );

  const howToSchemas = howToFAQs.map(faq => ({
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": faq.question,
    "description": faq.answer.substring(0, 160),
    "totalTime": "PT5M", // Assume 5 minutes for most tasks
    "supply": ["Computer or mobile device", "Internet connection"],
    "tool": ["SeniorThrive platform"],
    "step": [{
      "@type": "HowToStep",
      "name": "Access SeniorThrive",
      "text": "Log into your SeniorThrive account",
      "url": "https://seniorthrive.com"
    }]
  }));

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      {howToSchemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default FAQSchema;